import * as THREE from "three";

export interface ElementMetaEntry {
  type: string;
  mark: string;
  storeyName: string;
}

export interface StoreyLevel {
  id: number;
  name: string;
  elevation: number;
}

export interface FilterState {
  storeyId: string;
  types: string[];
  mark: string;
}

export interface IfcTab {
  id: number;
  title: string;
  signature: string;
  file: File;
  filterState: FilterState;
}

export interface AnnotationRecord {
  id: number;
  title: string;
  priority: string;
  deadline: string;
  descriptionHtml: string;
  elementIds: number[];
}

export interface MeasureHistoryItem {
  id: number;
  distance: number;
  object3d: THREE.Group;
  firstPoint: THREE.Vector3;
  secondPoint: THREE.Vector3;
  labelSprite: THREE.Sprite | null;
}

export interface IfcRevisionEntry {
  expressId: number;
  type: string;
  fingerprint: string;
}

export interface IfcTreeNode {
  expressId: number;
  type: string;
  name: string;
  children: IfcTreeNode[];
  hasGeometry: boolean;
}

export interface ClippingState {
  enabled: boolean;
  axisX: { enabled: boolean; value: number };
  axisY: { enabled: boolean; value: number };
  axisZ: { enabled: boolean; value: number };
  invertX: boolean;
  invertY: boolean;
  invertZ: boolean;
}

export interface SavedPlanView {
  storeyName: string;
  cutAxis: number;
  cut: number;
  zoom: number;
  fitHalf: number;
  position: [number, number, number];
  target: [number, number, number];
  up: [number, number, number];
}

export interface SavedView {
  id: number;
  name: string;
  thumbnail: string;
  cameraPosition: [number, number, number];
  cameraTarget: [number, number, number];
  cameraUp: [number, number, number];
  hiddenExpressIds: number[];
  filterState: FilterState;
  clipping: ClippingState;
  colorRule: ColorRule | null;
  createdAt: number;
  /** Задано, если вид сохранён из плана сверху, а не из 3D. */
  plan: SavedPlanView | null;
}

export type ColorRuleMode = "none" | "type" | "storey" | "mark";

export interface ColorRule {
  mode: ColorRuleMode;
  palette: Record<string, string>;
}

export const MAX_IFC_TABS = 3;

export const SIDE_RAIL_EXPANDED_KEY = "ifc-viewer.side-rail-expanded";
export const SIDE_RAIL_SECTION_KEY = "ifc-viewer.side-rail-section";

export const ANNOTATION_STORAGE_PREFIX = "ifc-web-viewer.annotations.v1:";
export const VIEWS_STORAGE_PREFIX = "ifc-web-viewer.views.v1:";

export const PICK_CUSTOM_ID = "ifc-selected";
export const HIDDEN_GHOST_ID = "ifc-hidden-ghost";
export const COMPARE_ADDED_ID = "ifc-compare-added";
export const COMPARE_CHANGED_ID = "ifc-compare-changed";
export const COLOR_RULE_SUBSET_PREFIX = "ifc-color-rule:";

export const PRIORITY_LABEL_RU: Record<string, string> = {
  suspended: "Приостановлено",
  low: "Низкий",
  normal: "Обычный",
  high: "Высокий",
  critical: "Критичный",
};

/**
 * Глобальный изменяемый state приложения. На этапе 0 это singleton-объект,
 * на следующих этапах он будет заменён нормальным AppStore с подписками.
 */
export const state = {
  ifcModel: null as any,
  /** ID модели в FragmentsManager (обычно имя файла). */
  fragmentsModelId: null as string | null,
  /** Express/local IDs элементов с геометрией (для дерева и фильтров). */
  geometryExpressIds: null as number[] | null,
  xrayEnabled: false,
  pointerDownPos: null as { x: number; y: number } | null,
  /**
   * Режим «мультивыбор»: пока включён, каждый клик/тап по элементу ведёт
   * себя так, как будто зажат Ctrl (добавляет/убирает элемент из выделения,
   * не сбрасывая остальное). Нужен как замена Ctrl+клик на мобильных
   * устройствах, где нет клавиатуры — переключается кнопкой в разделе
   * «Модель» (см. #multiselect-toggle).
   */
  multiSelectMode: false,
  selectedExpressIds: new Set<number>(),
  hiddenExpressIds: new Set<number>(),
  elementMeta: new Map<number, ElementMetaEntry>(),
  /** Этажи модели с отметкой IfcBuildingStorey.Elevation, по возрастанию. */
  storeyLevels: [] as StoreyLevel[],
  activeFilters: { storeyId: "", types: [] as string[], mark: "" } as FilterState,

  measureMode: false,
  pendingMeasurePoint: null as THREE.Vector3 | null,
  pendingMeasureMarker: null as THREE.Mesh | null,
  measureCounter: 1,
  measureHistory: [] as MeasureHistoryItem[],
  measureUnit: "cm" as "cm" | "mm",
  measureSnapMode: "surface" as "surface" | "vertex" | "edge" | "center",

  currentIfcRevision: null as Map<string, IfcRevisionEntry> | null,
  baselineIfcRevision: null as Map<string, IfcRevisionEntry> | null,
  baselineIfcFileName: "",
  // Сырой файл базовой ревизии — нужен, чтобы по требованию (галочка
  // «показывать удалённые красным») построить геометрию удалённых элементов.
  baselineIfcFile: null as File | null,
  // Показывать ли удалённые элементы (есть только в базе) красным «призраком».
  showRemovedEnabled: false,
  // Express ID (в координатах базового файла) удалённых элементов с прошлого
  // сравнения — чтобы перестроить призрак при переключении галочки.
  lastRemovedExpressIds: [] as number[],

  transparentSpaceIds: new Set<number>(),
  transparentSpaceMesh: null as THREE.Mesh | null,

  ifcTabs: [] as IfcTab[],
  activeIfcTabId: null as number | null,
  ifcTabIdCounter: 1,
  pendingOpenMode: "smart" as "smart" | "new-tab" | "replace-active",

  annotations: [] as AnnotationRecord[],
  annotationIdCounter: 1,
  editingAnnotationId: null as number | null,
  activeAnnotationImage: null as HTMLImageElement | null,

  currentIfcFileKey: null as string | null,
  currentIfcFileName: "",

  ifcAddMenuOpen: false,
  pendingModelSource: null as null | "cde" | "pc" | "presentation",
  pendingPresentationId: null as string | null,

  sideRailExpanded: true,

  ifcTree: null as IfcTreeNode | null,
  treeVisibilityOverrides: new Set<number>(),

  clipping: {
    enabled: false,
    axisX: { enabled: false, value: 0 },
    axisY: { enabled: false, value: 0 },
    axisZ: { enabled: false, value: 0 },
    invertX: false,
    invertY: false,
    invertZ: false,
  } as ClippingState,

  modelBounds: null as { min: [number, number, number]; max: [number, number, number] } | null,

  /** Вид сверху. Пока включён, рендер смотрит ортокамерой и режет этаж одной плоскостью. */
  planView: {
    active: false,
    storeyName: "",
    cutAxis: 1,
    cut: 0,
  },

  savedViews: [] as SavedView[],
  viewIdCounter: 1,

  activeColorRule: null as ColorRule | null,
  colorRuleSubsetIds: [] as string[],
};
